
import { useState, useCallback } from 'react';
import { useToast } from "@/components/ui/use-toast";
import { Message } from '@/types/chat';
import { useUserAccess } from './useUserAccess';
import { useChatMessages } from './chat/useChatMessages';
import { useReportProcessing } from './chat/useReportProcessing';
import { useSubscriptionChecks } from './chat/useSubscriptionChecks';
import { useSessionChecks } from './useSessionChecks';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Main hook for managing chat state, message handling, and report generation
 */
export const useChatState = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const { toast: uiToast } = useToast();
  const { checkUserAccess, markFreeReportUsed } = useUserAccess();
  
  // Use our new modular hooks
  const { 
    messages, 
    setMessages, 
    isTransitioning, 
    fadingIn,
    isLoadingChatHistory,
    initializeChatHistory,
    saveChatHistoryAndUpdateUrl
  } = useChatMessages();
  
  const {
    handleAdminMessage,
    processUserMessage,
    checkRateLimit
  } = useReportProcessing();
  
  const {
    handleNoReportsRemaining,
    handleFreeReportUsed,
    checkSubscriptionLimits
  } = useSubscriptionChecks();
  
  const { verifyUserSession } = useSessionChecks();

  /**
   * Double check the user's report limit directly from the database
   * This is a critical function to ensure we're not exceeding limits
   */
  const checkReportLimitDirectly = async (userId: string, userType: string) => {
    try {
      // Get report count from database
      const { data: reportUsage, error } = await supabase
        .from('report_usage')
        .select('reports_generated')
        .eq('user_id', userId)
        .single();
        
      if (error && error.code !== 'PGRST116') {
        console.error('Error checking report limit directly:', error);
        return { hasReachedLimit: false, reportCount: 0 }; // Error on the side of caution
      }
      
      const reportCount = reportUsage?.reports_generated || 0;
      console.log(`DIRECT DB CHECK: User ${userId} of type ${userType} has used ${reportCount} reports`);
      
      // Check against plan limits
      let hasReachedLimit = false;
      
      if (userType === 'free' && reportCount >= 2) {
        hasReachedLimit = true;
      } else if (userType === 'starter' && reportCount >= 10) {
        hasReachedLimit = true;
      } else if (userType === 'pro' && reportCount >= 30) {
        hasReachedLimit = true;
      } else if (userType === 'premium' && reportCount >= 60) {
        hasReachedLimit = true;
      }
      
      return { hasReachedLimit, reportCount };
    } catch (err) {
      console.error('Unexpected error in direct report limit check:', err);
      return { hasReachedLimit: false, reportCount: 0 };
    }
  };

  /**
   * Checks if a message is likely to be a new report request rather than a follow-up question
   * @param message The user's message to analyze
   * @returns boolean indicating if this is likely a new report request
   */
  const isNewReportRequest = (message: string): boolean => {
    // Consider a message a new report request if:
    // 1. It's not a follow-up or thank you message
    // 2. It has sufficient length to be a report query
    return !message.startsWith('Please provide more details') && 
           !message.startsWith('Can you explain') &&
           !message.toLowerCase().includes('thank') &&
           message.length > 10;
  };

  /**
   * Handles sending a new user message
   * Manages authentication, user access checking, and report generation
   * Now ensures consistent behavior for both admin and regular users
   */
  const handleSendMessage = useCallback(async (message: string) => {
    // Special case for when the user has reached their plan limits
    if (message === "__SHOW_PRICING_SHEET__") {
      handleNoReportsRemaining(setMessages);
      setShowPricing(true);
      return;
    }
    
    // Verify user session is valid before proceeding
    const sessionValid = await verifyUserSession();
    if (!sessionValid) return;
    
    setIsLoading(true);
    
    try {
      // Check user access level and permissions BEFORE adding the message to the state
      const userAccess = await checkUserAccess();
      const { user, userType, isAdmin, reportCount, usedFreeReport } = userAccess || {};
      
      if (!user) {
        toast.error("Your session has expired. Please sign in again.");
        setIsLoading(false);
        return;
      }
      
      // Check if this is a new report request and clear previous messages if so
      const shouldClearChat = isNewReportRequest(message);
      const baseMessages = shouldClearChat ? [] : [...messages];
      
      // Now add their message to the state
      const newMessages: Message[] = [...baseMessages, { role: 'user' as const, content: message }];
      setMessages(newMessages);
      
      let response, success;
      
      // For all users, regardless of admin status, process the message the same way
      if (isAdmin) {
        const result = await handleAdminMessage(message, newMessages, user);
        response = result.response;
        success = result.success;
      } else {
        // Check limits for non-admin users
        const { hasReachedLimit } = await checkReportLimitDirectly(user.id, userType);
        
        if (hasReachedLimit) {
          console.log(`LIMIT ENFORCED: User ${user.id} has reached their ${userType} plan limit`);
          handleNoReportsRemaining(setMessages);
          setShowPricing(true);
          setIsLoading(false);
          return;
        }
        
        // Check if this is their first report
        const isFirstReport = !usedFreeReport && userType === 'free' && reportCount === 0;
        
        // For non-admins, check subscription limits
        const limitsOk = checkSubscriptionLimits(userType, reportCount || 0, messages.length, usedFreeReport || false, setMessages);
        
        if (!limitsOk) {
          setShowPricing(true);
          setIsLoading(false);
          return;
        }
        
        // Check rate limits
        const rateLimitCheck = await checkRateLimit();
        if (rateLimitCheck.limited) {
          setMessages(prev => [...prev, {
            role: 'assistant' as const,
            content: rateLimitCheck.message
          }]);
          setIsLoading(false);
          return;
        }
        
        const result = await processUserMessage(message, newMessages, isFirstReport || false);
        response = result.response;
        success = result.success;
        
        // If this was their first report, mark it as used
        if (isFirstReport && success) {
          await markFreeReportUsed();
          
          // Inform the user that they've used their free report
          uiToast({
            title: "Free Report Used",
            description: "You've used your free report. To generate more reports, you'll need to upgrade.",
            duration: 7000,
          });
        }
      }
      
      // Update messages with the response
      const updatedMessages: Message[] = [...newMessages, { role: 'assistant' as const, content: response }];
      setMessages(updatedMessages);
      
      // Save chat history for all users (admin or not)
      if (success && user) {
        try {
          await saveChatHistoryAndUpdateUrl(user.id, message, updatedMessages);
        } catch (saveError) {
          console.error('Error saving chat history:', saveError);
          // Continue even if save fails
        }
      } else if (!success) {
        uiToast({
          title: "Report Generation Issue",
          description: "We couldn't generate your report. This might be due to temporary service issues. Please try again later.",
          variant: "destructive",
          duration: 7000,
        });
      }
      
    } catch (error) {
      console.error('Error processing request:', error);
      setMessages(prev => [...prev, {
        role: 'assistant' as const,
        content: "I apologize, but I encountered an error while processing your request. This could be due to network issues or service availability. Please try again later."
      }]);
      uiToast({
        title: "Connection Error",
        description: "Failed to connect to our AI services. Please check your internet connection and try again.",
        variant: "destructive",
        duration: 7000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [messages, setMessages, verifyUserSession, checkUserAccess, handleAdminMessage, 
      processUserMessage, checkRateLimit, saveChatHistoryAndUpdateUrl, markFreeReportUsed,
      handleNoReportsRemaining, handleFreeReportUsed, checkSubscriptionLimits, uiToast,
      checkReportLimitDirectly]);

  return {
    messages,
    isLoading,
    isTransitioning,
    showPricing,
    setShowPricing,
    fadingIn,
    isLoadingChatHistory,
    initializeChatHistory,
    handleSendMessage
  };
};
